import React, { useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import * as ss from 'simple-statistics';
import { AlertTriangle, TrendingUp, Hash, Percent, CheckCircle2 } from 'lucide-react';

const DataAnalytics = ({ data, headers }) => {
    // Calculate comprehensive analytics
    const analytics = useMemo(() => {
        if (!data || data.length === 0 || !headers || headers.length === 0) {
            return null;
        }

        const totalRows = data.length;
        const totalCells = totalRows * headers.length;

        // Calculate stats per column (skip _rowId)
        const columnStats = headers
            .filter(header => header !== '_rowId')
            .map((header) => {
                const columnData = data.map(row => row[header]);
                const nonEmptyData = columnData.filter(val => val !== '' && val !== null && val !== undefined);
                const missingCount = totalRows - nonEmptyData.length;

                // Detect if column is numeric
                const numericData = nonEmptyData
                    .map(val => parseFloat(val))
                    .filter(val => !isNaN(val));

                const isNumeric = numericData.length > nonEmptyData.length * 0.7; // 70% threshold

                let stats = {
                    name: header,
                    type: isNumeric ? 'numeric' : 'categorical',
                    totalValues: nonEmptyData.length,
                    missingCount,
                    missingPercent: ((missingCount / totalRows) * 100).toFixed(1),
                    uniqueCount: new Set(nonEmptyData).size
                };

                if (isNumeric && numericData.length > 0) {
                    // Numeric column statistics
                    const mean = ss.mean(numericData);
                    const median = ss.median(numericData);
                    const stdDev = ss.standardDeviation(numericData);
                    const min = ss.min(numericData);
                    const max = ss.max(numericData);

                    // Outlier detection using IQR method
                    const q1 = ss.quantile(numericData, 0.25);
                    const q3 = ss.quantile(numericData, 0.75);
                    const iqr = q3 - q1;
                    const lowerBound = q1 - 1.5 * iqr;
                    const upperBound = q3 + 1.5 * iqr;

                    const outliers = numericData.filter(val => val < lowerBound || val > upperBound);

                    stats = {
                        ...stats,
                        mean: mean.toFixed(2),
                        median: median.toFixed(2),
                        stdDev: stdDev.toFixed(2),
                        min: min.toFixed(2),
                        max: max.toFixed(2),
                        q1: q1.toFixed(2),
                        q3: q3.toFixed(2),
                        outlierCount: outliers.length,
                        outlierPercent: ((outliers.length / numericData.length) * 100).toFixed(1)
                    };
                } else {
                    // Categorical column statistics
                    const frequencies = {};
                    nonEmptyData.forEach(val => {
                        frequencies[val] = (frequencies[val] || 0) + 1;
                    });

                    const topValues = Object.entries(frequencies)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 5)
                        .map(([value, count]) => ({
                            value: String(value).substring(0, 20), // Truncate long values
                            count,
                            percent: ((count / nonEmptyData.length) * 100).toFixed(1)
                        }));

                    stats = {
                        ...stats,
                        topValues,
                        distribution: topValues
                    };
                }

                return stats;
            });

        // Overall dataset metrics
        const totalMissing = columnStats.reduce((sum, col) => sum + col.missingCount, 0);
        const totalOutliers = columnStats
            .filter(col => col.type === 'numeric')
            .reduce((sum, col) => sum + (col.outlierCount || 0), 0);

        return {
            totalRows,
            totalColumns: headers.length,
            totalCells,
            totalMissing,
            missingPercent: ((totalMissing / totalCells) * 100).toFixed(1),
            totalOutliers,
            columnStats
        };
    }, [data, headers]);

    if (!analytics) {
        return (
            <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                    <TrendingUp size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No data to analyze</p>
                    <p className="text-sm mt-2">Generate or upload data to see analytics</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-gray-50/50">
            {/* Overview Cards */}
            <div className="p-4 space-y-3">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                    <TrendingUp size={16} className="text-blue-600" />
                    Dataset Overview
                </h3>

                <div className="grid grid-cols-2 gap-3">
                    <StatCard
                        icon={<Hash size={16} />}
                        label="Total Rows"
                        value={analytics.totalRows.toLocaleString()}
                        color="blue"
                    />
                    <StatCard
                        icon={<Hash size={16} />}
                        label="Columns"
                        value={analytics.totalColumns}
                        color="blue"
                    />
                    <StatCard
                        icon={<AlertTriangle size={16} />}
                        label="Missing Values"
                        value={`${analytics.totalMissing} (${analytics.missingPercent}%)`}
                        color={analytics.totalMissing > 0 ? "orange" : "green"}
                    />
                    <StatCard
                        icon={<AlertTriangle size={16} />}
                        label="Outliers"
                        value={analytics.totalOutliers}
                        color={analytics.totalOutliers > 0 ? "red" : "green"}
                    />
                </div>
            </div>

            {/* Per-Column Analysis */}
            <div className="px-4 pb-4 space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                    Column Analysis
                </h3>

                {analytics.columnStats.map((col, idx) => (
                    <div key={idx} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                        {/* Column Header */}
                        <div className="bg-gradient-to-r from-blue-50 to-white px-4 py-3 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="font-semibold text-gray-900">{col.name}</h4>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {col.type === 'numeric' ? '🔢 Numeric' : '🏷️ Categorical'} • {col.uniqueCount} unique values
                                    </p>
                                </div>
                                {col.missingCount > 0 && (
                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                                        {col.missingPercent}% missing
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="p-4">
                            {col.type === 'numeric' ? (
                                <NumericColumnStats stats={col} />
                            ) : (
                                <CategoricalColumnStats stats={col} />
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Helper Components
const StatCard = ({ icon, label, value, color = "blue" }) => {
    const colorClasses = {
        blue: "bg-blue-50 text-blue-600 border-blue-100",
        green: "bg-green-50 text-green-600 border-green-100",
        orange: "bg-orange-50 text-orange-600 border-orange-100",
        red: "bg-red-50 text-red-600 border-red-100"
    };

    return (
        <div className={`p-3 rounded-lg border ${colorClasses[color]}`}>
            <div className="flex items-center gap-2 mb-1">
                {icon}
                <span className="text-xs font-medium opacity-75">{label}</span>
            </div>
            <div className="text-lg font-bold">{value}</div>
        </div>
    );
};

const NumericColumnStats = ({ stats }) => {
    return (
        <div className="space-y-3">
            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                    <div className="text-gray-500">Mean</div>
                    <div className="font-semibold text-gray-900">{stats.mean}</div>
                </div>
                <div>
                    <div className="text-gray-500">Median</div>
                    <div className="font-semibold text-gray-900">{stats.median}</div>
                </div>
                <div>
                    <div className="text-gray-500">Std Dev</div>
                    <div className="font-semibold text-gray-900">{stats.stdDev}</div>
                </div>
                <div>
                    <div className="text-gray-500">Min</div>
                    <div className="font-semibold text-gray-900">{stats.min}</div>
                </div>
                <div>
                    <div className="text-gray-500">Max</div>
                    <div className="font-semibold text-gray-900">{stats.max}</div>
                </div>
                <div>
                    <div className="text-gray-500">Range</div>
                    <div className="font-semibold text-gray-900">{(stats.max - stats.min).toFixed(2)}</div>
                </div>
            </div>

            {/* Outlier Warning */}
            {stats.outlierCount > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-2 flex items-start gap-2">
                    <AlertTriangle size={14} className="text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs">
                        <span className="font-semibold text-red-900">
                            {stats.outlierCount} outlier{stats.outlierCount > 1 ? 's' : ''} detected
                        </span>
                        <span className="text-red-700"> ({stats.outlierPercent}%)</span>
                        <p className="text-red-600 mt-1">
                            Values outside [{stats.q1} - {stats.q3}] range
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

const CategoricalColumnStats = ({ stats }) => {
    if (!stats.topValues || stats.topValues.length === 0) {
        return <div className="text-xs text-gray-400">No data available</div>;
    }

    const chartData = stats.topValues.map(item => ({
        name: item.value,
        value: item.count
    }));

    const maxCount = Math.max(...stats.topValues.map(v => v.count));

    return (
        <div className="space-y-3">
            <div className="text-xs font-medium text-gray-600">Top Values</div>

            {/* Bar Chart Visualization */}
            <div className="space-y-2">
                {stats.topValues.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                        <div className="text-xs text-gray-600 w-24 truncate" title={item.value}>
                            {item.value}
                        </div>
                        <div className="flex-grow h-6 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-blue-500 transition-all duration-500 flex items-center justify-end pr-2"
                                style={{ width: `${(item.count / maxCount) * 100}%` }}
                            >
                                <span className="text-xs font-semibold text-white">{item.count}</span>
                            </div>
                        </div>
                        <div className="text-xs text-gray-500 w-12 text-right">
                            {item.percent}%
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DataAnalytics;
